import React, { useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Lock, Unlock, Play, SkipForward, TrendingUp, TrendingDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { ComboSuggestion, SimulationStep } from '@/services/pattern-combo-analytics-service';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

interface ComboSimulationPreviewProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  combo: ComboSuggestion;
}

export const ComboSimulationPreview: React.FC<ComboSimulationPreviewProps> = ({
  open,
  onOpenChange,
  combo
}) => {
  // Calculate rolling win rate for chart
  const chartData = useMemo(() => {
    const data: Array<{ index: number; winRate: number }> = [];
    const trades = combo.simulationResult.trades;
    const windowSize = 10;
    
    for (let i = windowSize; i <= trades.length; i++) {
      const window = trades.slice(i - windowSize, i);
      const wins = window.filter(t => t.win === 1).length;
      const winRate = (wins / windowSize) * 100;
      data.push({ index: i, winRate });
    }
    
    return data;
  }, [combo]);

  // Get first 100 steps for timeline
  const timelineSteps = useMemo(() => {
    const allSteps: SimulationStep[] = [];
    let recentResults = '';
    let mode: 'OPEN' | 'GATE' = 'OPEN';
    let activeLock = null;
    
    const sortedCombo = [...combo.rules].sort((a, b) => a.priority - b.priority);
    
    for (let idx = 0; idx < Math.min(100, combo.simulationResult.trades.length + 50); idx++) {
      const signal = combo.simulationResult.trades[idx]?.signal;
      if (!signal) break;
      
      // Reconstruct simulation steps
      if (signal.win !== null && (signal.action === 'EXIT_LONG' || signal.action === 'EXIT_SHORT')) {
        // Check lock
        if (mode === 'OPEN') {
          const matchedLock = sortedCombo.find(rule => recentResults.endsWith(rule.lock));
          if (matchedLock) {
            allSteps.push({
              signalIndex: idx,
              recentResults,
              mode: 'GATE',
              activeLock: matchedLock,
              action: 'LOCK',
              signal
            });
            mode = 'GATE';
            activeLock = matchedLock;
            recentResults += signal.win.toString();
            recentResults = recentResults.slice(-10);
            continue;
          }
        }
        
        // Check open
        if (mode === 'GATE' && activeLock) {
          if (recentResults.endsWith((activeLock as any).open)) {
            allSteps.push({
              signalIndex: idx,
              recentResults,
              mode: 'OPEN',
              activeLock: null,
              action: 'UNLOCK',
              signal
            });
            mode = 'OPEN';
            activeLock = null;
            recentResults += signal.win.toString();
            recentResults = recentResults.slice(-10);
            continue;
          } else {
            allSteps.push({
              signalIndex: idx,
              recentResults,
              mode: 'GATE',
              activeLock,
              action: 'SKIP',
              signal
            });
            recentResults += signal.win.toString();
            recentResults = recentResults.slice(-10);
            continue;
          }
        }
        
        // Trade
        allSteps.push({
          signalIndex: idx,
          recentResults,
          mode: 'OPEN',
          activeLock: null,
          action: 'TRADE',
          win: signal.win,
          signal
        });
        recentResults += signal.win.toString();
        recentResults = recentResults.slice(-10);
      }
    }
    
    return allSteps.slice(0, 100);
  }, [combo]);

  const getActionIcon = (action: string) => {
    switch (action) {
      case 'LOCK':
        return <Lock className="h-4 w-4 text-orange-500" />;
      case 'UNLOCK':
        return <Unlock className="h-4 w-4 text-green-500" />;
      case 'SKIP':
        return <SkipForward className="h-4 w-4 text-gray-500" />;
      case 'TRADE':
        return <Play className="h-4 w-4 text-blue-500" />;
      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle>Simulation Preview - Combo #{combo.rank}</DialogTitle>
          <DialogDescription>
            Detailed simulation timeline and performance metrics
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="h-[calc(90vh-120px)] pr-4">
          <div className="space-y-6">
            {/* Summary Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium">Win Rate</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{combo.winRate.toFixed(1)}%</div>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium">Total Trades</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{combo.totalTrades}</div>
                  <div className="text-xs text-muted-foreground">
                    <span className="text-green-600">{combo.wins}W</span>
                    {' / '}
                    <span className="text-red-600">{combo.losses}L</span>
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium">Lock Events</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{combo.lockActivations}</div>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium">Skipped Signals</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{combo.skippedSignals}</div>
                </CardContent>
              </Card>
            </div>

            {/* Win Rate Trend Chart */}
            {chartData.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm">Win Rate Trend (Rolling 10 Trades)</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={200}>
                    <LineChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                      <XAxis 
                        dataKey="index" 
                        label={{ value: 'Trade Number', position: 'insideBottom', offset: -5 }}
                        className="text-xs"
                      />
                      <YAxis 
                        label={{ value: 'Win Rate %', angle: -90, position: 'insideLeft' }}
                        className="text-xs"
                      />
                      <Tooltip 
                        formatter={(value: any) => `${value.toFixed(1)}%`}
                        labelFormatter={(label) => `Trade #${label}`}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="winRate" 
                        stroke="hsl(var(--primary))" 
                        strokeWidth={2}
                        dot={false}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            )}

            {/* Pattern Rules */}
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Pattern Rules</CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Priority</TableHead>
                      <TableHead>Lock Pattern</TableHead>
                      <TableHead>Open Pattern</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {combo.rules.map((rule, idx) => (
                      <TableRow key={idx}>
                        <TableCell>
                          <Badge variant="outline">{rule.priority}</Badge>
                        </TableCell>
                        <TableCell>
                          <code className="px-2 py-1 bg-muted rounded text-sm">
                            {rule.lock}
                          </code>
                        </TableCell>
                        <TableCell>
                          <code className="px-2 py-1 bg-muted rounded text-sm">
                            {rule.open}
                          </code>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            {/* Timeline */}
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Simulation Timeline (First 100 Events)</CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[400px]">
                  <div className="space-y-2">
                    {timelineSteps.map((step, idx) => (
                      <div
                        key={idx}
                        className={cn(
                          "flex items-center gap-3 p-2 rounded text-sm border",
                          step.action === 'TRADE' && step.win === 1 && "bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-900",
                          step.action === 'TRADE' && step.win === 0 && "bg-red-50 dark:bg-red-950/20 border-red-200 dark:border-red-900",
                          step.action === 'LOCK' && "bg-orange-50 dark:bg-orange-950/20 border-orange-200 dark:border-orange-900",
                          step.action === 'UNLOCK' && "bg-blue-50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-900",
                          step.action === 'SKIP' && "bg-muted border-muted"
                        )}
                      >
                        <div className="w-8 text-muted-foreground">#{idx + 1}</div>
                        <div className="flex-shrink-0">{getActionIcon(step.action)}</div>
                        <code className="px-2 py-0.5 bg-background rounded text-xs">
                          {step.recentResults || '∅'}
                        </code>
                        <Badge variant={step.mode === 'OPEN' ? 'default' : 'secondary'} className="text-xs">
                          {step.mode}
                        </Badge>
                        {step.action === 'LOCK' && step.activeLock && (
                          <span className="text-xs text-muted-foreground">
                            🔒 {step.activeLock.lock} | {step.activeLock.open}
                          </span>
                        )}
                        {step.action === 'TRADE' && step.win !== undefined && (
                          <div className="ml-auto flex items-center gap-1">
                            {step.win === 1 ? (
                              <TrendingUp className="h-4 w-4 text-green-600" />
                            ) : (
                              <TrendingDown className="h-4 w-4 text-red-600" />
                            )}
                            <span className={cn(
                              "font-medium",
                              step.win === 1 ? "text-green-600" : "text-red-600"
                            )}>
                              {step.win === 1 ? 'Win' : 'Loss'}
                            </span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};
